import { useTranslation } from 'react-i18next';
import { padNumber } from '@/helpers/utils';
import {
  BANK_REQUISITES,
  CONTACT_EMAIL,
  prettyMoney,
} from '@scramble/utils';
import { useModal } from '@/providers/modal';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { InlineAlert } from '@scramble/ui';
import { useIsMobile } from '@scramble/hooks';
import { useGetMe } from '@/hooks/useGetMe';
import { CopyButton } from '@/components/UI/CopyButton';
import IconInfo from '@scramble/design-system/icons/info-circle.svg?react'
import IconBank from '@scramble/design-system/icons/bank.svg'
import s from './index.module.scss';

const id = MODAL_IDS.bank_requisite_modal;

export const BankRequisiteModal = props => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal {...props} />;
};

export const Modal = ({ amount }) => {
  const { t } = useTranslation();
  const { user } = useGetMe();
  const userID = user?.id;
  const topUpAmount = Number(amount);

  const isMobile = useIsMobile();
  const { closeModal } = useModal();

  const content = (
    <div className={s.modal}>
      <div className={s.header}>
        <div className={s.icon}>
          <img src={IconBank} width={28} height={28} alt="bank" />
        </div>
        <div className={s.amount}>{prettyMoney(topUpAmount)}</div>
        <div className={s.amount_label}>
          {t('modal:bank_requisites_modal.payment_amount')}
        </div>
      </div>
      <div className={s.content}>
        <div className={s.content_inner}>
          <div style={{ padding: '0 12px' }}>
            <InlineAlert
              type="neutral"
              icon={<IconInfo />}
              message={t('modal:bank_requisites_modal.include_payment_details')}
            />
          </div>
          <ul className={s.list}>
            <li className={s.item}>
              <div className={s.item_content}>
                <div className={s.label}>
                  {t('modal:bank_requisites_modal.beneficiary_name')}
                </div>
                <div className={s.value}>{BANK_REQUISITES.beneficiaryName}</div>
              </div>
              <CopyButton text={BANK_REQUISITES.beneficiaryName} />
            </li>

            <li className={s.item}>
              <div className={s.item_content}>
                <div className={s.label}>
                  {t('modal:bank_requisites_modal.beneficiary_account_number')}
                </div>
                <div className={s.value}>{BANK_REQUISITES.visibleIban}</div>
              </div>
              <CopyButton text={BANK_REQUISITES.iban} />
            </li>

            <li className={s.item}>
              <div className={s.item_content}>
                <div className={s.label}>
                  {t('modal:bank_requisites_modal.payment_details')}
                </div>
                <div className={s.value}>{padNumber(userID)}</div>
              </div>
              <div className={s.item_end}>
                <span className={s.required}>
                  {t('modal:bank_requisites_modal.required')}
                </span>
                <CopyButton text={padNumber(userID)} />
              </div>
            </li>

            <li className={s.item}>
              <div className={s.item_content}>
                <div className={s.label}>
                  {t('modal:bank_requisites_modal.bank_swift_code')}
                </div>
                <div className={s.value}>{BANK_REQUISITES.bankSwiftCode}</div>
              </div>
              <CopyButton text={BANK_REQUISITES.bankSwiftCode} />
            </li>

            <li className={s.item}>
              <div className={s.item_content}>
                <div className={s.label}>
                  {t('modal:bank_requisites_modal.bank_name')}
                </div>
                <div className={s.value}>
                  {BANK_REQUISITES.beneficiaryBankName}
                </div>
              </div>
              <CopyButton text={BANK_REQUISITES.beneficiaryBankName} />
            </li>

            <li className={s.item}>
              <div className={s.item_content}>
                <div className={s.label}>
                  {t('modal:bank_requisites_modal.bank_address')}
                </div>
                <div className={s.value}>{BANK_REQUISITES.bankAddress}</div>
              </div>
              <CopyButton text={BANK_REQUISITES.bankAddress} />
            </li>

            <li className={s.item}>
              <div className={s.item_content}>
                <div className={s.label}>
                  {t('modal:bank_requisites_modal.company_email')}
                </div>
                <div className={s.value}>{CONTACT_EMAIL}</div>
              </div>
              <CopyButton text={CONTACT_EMAIL} />
            </li>
          </ul>
        </div>
      </div>
    </div>
  );

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalCenterLayout>
  );
};